import {
  ADMIN_ROOT,
  AdminLayoutRoot,
  NotFound,
  RouteObjectType,
  UserPermission,
  UserRole,
  adminAuthRoutes,
  useAuth,
  usePermissions,
} from '@vgrubs/components';
import { AnimatePresence } from 'framer-motion';
import { Navigate, Route, Routes, useLocation } from 'react-router';

function PrivateRoute({
  children,
  permissions,
  permission,
  routes,
}: {
  children: React.ReactNode;
  permissions: UserRole[];
  permission?: UserPermission;
  routes: RouteObjectType[];
}) {
  const role = useAuth((state) => state?.user?.role);
  const { can, isLoading: permissionsLoading } = usePermissions();

  // This doubles as the redirect target, so it has to apply the SAME two
  // checks the guard below does. Matching on role alone would send a user to a
  // route their permissions deny, which would bounce them straight back here,
  // forever. (No route is permission-gated today - chat dropped its gate when
  // groups and direct messages arrived, since membership is their only rule -
  // but the mechanism stays wired for the next one that needs it.)
  const firstAvailableRoute = routes.find(
    ({ permissions: routeRoles, permission: routePermission }) =>
      routeRoles.includes(role!) && (!routePermission || can(routePermission))
  );

  const roleAllowed = !!role && permissions.includes(role);

  // A permission-gated route can't decide anything until the permissions
  // request lands. Redirecting here would bounce a legitimate deep link before
  // the answer arrives; a failed request resolves to `can() === false` rather
  // than staying loading, so this can't hang.
  if (roleAllowed && permission && permissionsLoading) return null;

  const allowed = roleAllowed && (!permission || can(permission));

  return allowed ? (
    <>{children}</>
  ) : (
    <Navigate to={firstAvailableRoute?.path || '/'} />
  );
}

export const AppRouter = ({
  routes,
  authRoutes = adminAuthRoutes,
  sidebarTop,
}: {
  routes: RouteObjectType[];
  /** Unauthenticated screens; the shared admin login + OTP unless the app brings its own. */
  authRoutes?: { path: string; Page: React.ComponentType }[];
  /** Rendered inside the sidebar between the brand header and the navigation. */
  sidebarTop?: React.ReactNode;
}) => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes key={location.pathname} location={location}>
        <Route
          path={ADMIN_ROOT}
          element={<AdminLayoutRoot routes={routes} sidebarTop={sidebarTop} />}
        >
          {authRoutes.map(({ path, Page }, i) => (
            <Route key={i} path={path} element={<Page />} />
          ))}

          {routes.map(({ path, Page, permissions, permission }, i) => {
            return (
              <Route
                key={i}
                path={path}
                element={
                  <PrivateRoute
                    routes={routes}
                    permissions={permissions}
                    permission={permission}
                  >
                    {Page && <Page />}
                  </PrivateRoute>
                }
              />
            );
          })}

          <Route path={'/*'} element={<NotFound />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
};
